import { useState, useEffect } from "react";
import { Card, CardContent } from "./ui/card";
import { Heart, Sun, Smile, Cloud, Meh, Frown, TrendingUp, Crown } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";

const moodConfig = {
  grateful: { icon: Heart, color: "#F43F5E", label: "Grateful", bg: "bg-rose-100" },
  peaceful: { icon: Sun, color: "#F59E0B", label: "Peaceful", bg: "bg-amber-100" },
  hopeful: { icon: Smile, color: "#10B981", label: "Hopeful", bg: "bg-emerald-100" },
  reflective: { icon: Cloud, color: "#3B82F6", label: "Reflective", bg: "bg-blue-100" },
  neutral: { icon: Meh, color: "#64748B", label: "Neutral", bg: "bg-slate-100" },
  struggling: { icon: Frown, color: "#8B5CF6", label: "Struggling", bg: "bg-purple-100" },
};

const MoodTracker = ({ entries = [], isPremium = false }) => {
  const [moodData, setMoodData] = useState([]);
  const [dominantMood, setDominantMood] = useState(null);

  useEffect(() => {
    if (entries.length > 0) {
      // Count moods from last 30 entries
      const moodCounts = {};
      const recentEntries = entries.slice(0, 30);
      
      recentEntries.forEach(entry => {
        if (entry.mood) {
          moodCounts[entry.mood] = (moodCounts[entry.mood] || 0) + 1;
        }
      });

      // Convert to array for visualization
      const data = Object.entries(moodCounts).map(([mood, count]) => ({
        mood,
        count,
        config: moodConfig[mood] || moodConfig.neutral
      })).sort((a, b) => b.count - a.count);

      setMoodData(data);
      
      if (data.length > 0) {
        setDominantMood(data[0]);
      }
    }
  }, [entries]);

  // Get last 7 days mood timeline
  const getWeeklyMoods = () => {
    const weekMoods = [];
    const today = new Date();
    
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      
      const dayEntry = entries.find(e => 
        e.created_at.split('T')[0] === dateStr
      );
      
      weekMoods.push({
        day: date.toLocaleDateString('en-US', { weekday: 'short' }),
        mood: dayEntry?.mood || null,
        config: dayEntry?.mood ? moodConfig[dayEntry.mood] : null
      });
    }
    
    return weekMoods;
  };

  if (!isPremium) {
    return (
      <Card className="bg-card border-border/50 hover:border-primary/20 transition-all">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-5 w-5 text-primary" />
            <span className="text-sm uppercase tracking-[0.15em] text-muted-foreground font-semibold">Mood Tracker</span>
          </div>
          <div className="relative">
            <div className="blur-sm">
              <div className="flex gap-2 mb-4">
                {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, i) => (
                  <div key={i} className="flex-1 text-center">
                    <div className="text-xs text-muted-foreground mb-2">{day}</div>
                    <div className="w-8 h-8 rounded-full bg-muted mx-auto"></div>
                  </div>
                ))}
              </div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm rounded-lg">
              <div className="text-center p-4">
                <Crown className="h-6 w-6 text-accent mx-auto mb-2" />
                <p className="text-sm font-medium text-primary">Premium Feature</p>
                <Link to="/subscription">
                  <Button size="sm" className="mt-2 rounded-full bg-accent text-accent-foreground hover:bg-accent/90" data-testid="mood-upgrade-btn">
                    Unlock
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const weeklyMoods = getWeeklyMoods();
  const totalEntries = moodData.reduce((sum, m) => sum + m.count, 0);

  return (
    <Card className="bg-card border-border/50 hover:border-primary/20 transition-all">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            <span className="text-sm uppercase tracking-[0.15em] text-muted-foreground font-semibold">Mood Tracker</span>
          </div>
          {dominantMood && (
            <div className={`flex items-center gap-1 px-2 py-1 rounded-full ${dominantMood.config.bg}`}>
              <dominantMood.config.icon className="h-3 w-3" style={{ color: dominantMood.config.color }} />
              <span className="text-xs font-medium" style={{ color: dominantMood.config.color }}>
                {dominantMood.config.label}
              </span>
            </div>
          )}
        </div>

        {/* Weekly Timeline */}
        <div className="mb-6">
          <p className="text-xs text-muted-foreground mb-3">This Week</p>
          <div className="flex gap-2">
            {weeklyMoods.map((day, i) => (
              <div key={i} className="flex-1 text-center">
                <div className="text-xs text-muted-foreground mb-2">{day.day}</div>
                <div 
                  className={`w-8 h-8 rounded-full mx-auto flex items-center justify-center transition-all ${
                    day.config ? day.config.bg : 'bg-muted'
                  }`}
                >
                  {day.config ? (
                    <day.config.icon className="h-4 w-4" style={{ color: day.config.color }} />
                  ) : (
                    <div className="w-2 h-2 rounded-full bg-muted-foreground/30"></div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mood Distribution */}
        {moodData.length > 0 && (
          <div>
            <p className="text-xs text-muted-foreground mb-3">Mood Distribution ({totalEntries} entries)</p>
            <div className="space-y-2">
              {moodData.slice(0, 4).map((item) => (
                <div key={item.mood} className="flex items-center gap-3">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${item.config.bg}`}>
                    <item.config.icon className="h-3 w-3" style={{ color: item.config.color }} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium">{item.config.label}</span>
                      <span className="text-xs text-muted-foreground">{Math.round((item.count / totalEntries) * 100)}%</span>
                    </div>
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all duration-500"
                        style={{ 
                          width: `${(item.count / totalEntries) * 100}%`,
                          backgroundColor: item.config.color 
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {moodData.length === 0 && (
          <div className="text-center py-4">
            <p className="text-sm text-muted-foreground">Start journaling to track your moods</p>
            <Link to="/journal">
              <Button size="sm" variant="ghost" className="mt-2" data-testid="start-journaling-btn">
                Write First Entry →
              </Button>
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MoodTracker;
